<template>
  <div>
    <tx-action txType="Bridge" />
    <div class="d-flex justify-center mt-16">
      <div class="deposit-box pa-10">
        <c-tx-steps
          class="mt-6"
          borderType="stake"
          :options="steps"
          :current="currentStep"
          @setStep="s => (currentStep = s)"
        />
        <div v-if="currentStep === 1" class="p-relative" style="height: 80%">
          <c-loading v-if="availableBalanceState.fetching" />
          <div class="d-flex">
            <div class="flex-2">
              <div class="d-flex align-center mt-2">
                <span class="heading-5 font-weight-medium mr-4">From</span>
                <img :src="fromEcosystem.logo" alt="" height="40" width="40" />
                <div class="ml-2 heading-5 font-weight-medium">
                  {{ fromEcosystem.name }}
                </div>
              </div>
            </div>
            <div class="flex-2">
              <div class="d-flex align-center mt-2">
                <span class="heading-5 font-weight-medium mr-4">To</span>
                <img :src="currentProject.logo" alt="" height="40" width="40" />
                <div class="ml-2 heading-5 font-weight-medium">
                  dYdX Chain
                </div>
              </div>
            </div>
          </div>
          <div class="d-flex justify-space-between align-start mt-16">
            <enter-amount
              class="flex-1"
              v-model="bridgeAmount"
              :maxAmount="availableBalance"
              :suffix="currentProject.staking.token"
            />
          </div>
          <div class="justify-space-between align-start mt-5">
            <span class="text-5 font-weight-medium mr-4 mb-5">To Address:</span>
            <c-btn v-if="!walletSelected.address"
              @click="connect"
              >Connect Keplr address</c-btn>
            <get-address
              v-if="fetchAddress && keplrWallet && !walletSelected.address && !errorConnecting"
              :wallet="keplrWallet"
              :project="projectSelected"
              @onAddressSelected="onAddressSelected"
              @onError="onError"
            />
            <span class="font-weight-medium mb-5 info--text">{{ walletSelected.address }}</span>
          </div>
          <div class="d-flex mt-4 text-5 darkgray--text warning--text">
            <span class="font-weight-medium ">
              dYdX Chain settlement ~40 hours
            </span>
          </div>
          <div class="mt-16">
            <c-btn
              class="width-100"
              @click="next"
              :disabled="btnNextDisabled"
              walletRequired
              >Continue</c-btn
            >
          </div>
        </div>
        <approve
          v-if="currentStep === 2"
          :walletAccount="secondaryWalletSelected"
          class="mt-16"
          :getRawTx="getApprovalRawTx"
          broadcastAction="staking/dydx/broadcastTx"
          @next="currentStep = 3"
        />
        <confirm
          v-if="currentStep === 3"
          class="mt-16"
          :bridgeAmount="bridgeAmount"
          @close="$emit('close')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import BN from "bignumber.js";
import TxAction from "@/views/stake/components/tx/TxAction.vue";
import EnterAmount from "@/views/stake/components/tx/EnterAmount.vue";
import Approve from "@/views/stake/components/tx/EthApprove.vue";
import Confirm from "./Confirm.vue";
import SelectEcosystem from "@/views/stake/components/tx/SelectEcosystem.vue";
import SelectAsset from "@/views/stake/components/tx/SelectAsset.vue";
import ECOSYSTEMS from "@/common/config/ecosystems";
import WALLETS from "@/common/config/wallets";
import PROJECTS from "@/common/config/projects";
import GetAddress from "@/components/wallet/GetAddress";

export default {
  components: {
    TxAction,
    EnterAmount,
    Approve,
    Confirm,
    SelectEcosystem,
    SelectAsset,
    GetAddress,
  },
  computed: {
    ...mapGetters({
      walletSelected: "userWallets/walletSelected",
      secondaryWalletSelected: "userWallets/secondaryWalletSelected",
      currentProject: "projectConfig/latestProject",
      getTokenPriceInUsd: "appConfig/getTokenPriceInUsd",
      availableBalanceState: "staking/dydx/availableBalance",
      approvalRequired: "staking/dydx/needUnlockAssets",
      walletList: "userWallets/walletList",
    }),
    availableBalance() {
      return Number(this.availableBalanceState.result?.balance || 0);
    },
    balanceUsd() {
      return (
        this.getTokenPriceInUsd(this.currentProject.staking.token) *
        this.availableBalance
      );
    },
    isSufficentAmount() {
      return (
        BN(this.bridgeAmount).gt(0)  && new BN(this.bridgeAmount).isLessThanOrEqualTo(this.availableBalance)
      );
    },
    amountUsd() {
      return (
        this.getTokenPriceInUsd(this.currentProject.staking.token) *
        this.bridgeAmount
      );
    },
    btnNextDisabled() {
      if (!this.currentProject.status?.active) return true;
      return !this.isSufficentAmount;
    },
    btnStakeDisabled() {
      return this.gasOption.gas <= 0 || this.gasOption.gasPrice <= 0;
    },
  },
  data: () => ({
    keplrWallet: WALLETS.find(w => w.name === "keplr"),
    projectSelected: PROJECTS.find(p => p.name === "dydx"),
    fetchAddress: false,
    bridgeAmount: 0,
    errorConnecting: null,
    fromEcosystem: ECOSYSTEMS.find(
      item => item.name.toLowerCase() === "ethereum"
    ),
    steps: [
      {
        step: 1,
        text: "Amount",
      },
      {
        step: 2,
        text: "Approve",
        hidden: true,
      },
      {
        step: 3,
        text: "Confirm",
      },
    ],
    currentStep: 1,
    gasOption: {},
  }),
  methods: {
    getApprovalRawTx() {
      return this.$store.dispatch("staking/dydx/getApprovalRawTx");
    },
    next() {
      if (this.approvalRequired) {
        return (this.currentStep = 2);
      }
      this.currentStep = 3;
    },
    async onAddressSelected(address, metadata) {
      await this.$store.dispatch("userWallets/addWallet", {
        accountLabel: this.keplrWallet.text + " " + address.slice(-4),
        accountType: this.keplrWallet.type || this.keplrWallet.name,
        address: address,
        network: this.projectSelected.config.network,
        metadata,
      });
      this.$store.dispatch("addAlert", {
        type: "success",
        title: "Success",
        message: "Your keplr wallet has successfully connected.",
        timeout: 3000,
      });
      const newWallet = this.walletList.result.find(
        (w) => w.address.toLowerCase() === address.toLowerCase()
      );
      this.$store.dispatch("userWallets/setCurrentWallet", newWallet);
    },
    connect() {
      this.fetchAddress = true;
    },
    onError(error) {
      this.errorConnecting = error;
    },
  },
  watch: {
    approvalRequired: {
      immediate: true,
      handler(value) {
        if (!value) {
          this.steps[1].hidden = true;
        } else {
          this.steps[1].hidden = false;
        }
      },
    },
    ["secondaryWalletSelected.address"]() {
      this.currentStep = 1;
      this.bridgeAmount = 0;
    },
  },
};
</script>
